import { useState, useEffect, useCallback } from 'react'

import { Modal, Textarea, TextInput } from '@mantine/core'
import { useOs } from '@mantine/hooks'
import dayjs from 'dayjs'

import { navigate, routes } from '@redwoodjs/router'
import { useMutation, useQuery } from '@redwoodjs/web'

import { useAuth } from 'src/auth'
import DNButton from 'src/components/Buttons/Button/DNButton'
import {
  AssigneeMenu,
  PriorityMenu,
  TypeMenu,
} from 'src/components/Shared/TaskMenus'
import { useApplicantDrawerContext } from 'src/context/ApplicantDrawerContext'
import { useTaskModalContext } from 'src/context/TaskModalContext'
import { CREATE_TASK, GET_TASK, UPDATE_TASK } from 'src/graphql/tasks.graphql'
import { TaskPriority, TaskStatus, TaskType } from 'src/graphql/types/tasks'
import IconCornerDownRight from 'src/icons/IconCornerDownRight'
import { cn } from 'src/utils'

import { toast } from '../Toast/Toast'

import ApplicantMenu from './components/ApplicantMenu'
import DueControl from './components/DueControl'
import ReminderControl from './components/ReminderControl'
import StatusMenu from './components/StatusMenu'
import TaskTemplatePicker from './components/TaskTemplatePicker'
import { computeDefaultReminder } from './lib/dueDatePresets'

const defaultDueDate = () =>
  dayjs().add(1, 'day').hour(17).minute(0).second(0).millisecond(0).toDate()

// A properties-rail row: label on the left, control/value on the right.
const RailRow = ({ label, children, align = 'center' }) => (
  <div
    className={cn(
      'flex justify-between gap-2',
      align === 'start' ? 'items-start' : 'items-center'
    )}
  >
    <span className="flex-shrink-0 pt-1 text-xs text-doubleNickel-gray-500">
      {label}
    </span>
    <div className="flex min-w-0 flex-col items-end">{children}</div>
  </div>
)

const TaskModal = () => {
  const {
    opened,
    taskId,
    handleClose,
    callRefetch,
    applicant: initialApplicant,
  } = useTaskModalContext()
  const os = useOs()
  const isEdit = taskId !== null
  const { currentUser } = useAuth()
  // Lists keyed on the drawer's refreshKey (Applicants page) refetch after a save
  const applicantDrawer = useApplicantDrawerContext()
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState(TaskStatus.NOT_STARTED)
  const [priority, setPriority] = useState(TaskPriority.LOW)
  const [type, setType] = useState(TaskType.OTHER)
  const [dueDate, setDueDate] = useState(() => defaultDueDate())
  const [reminderDate, setReminderDate] = useState(() =>
    computeDefaultReminder(defaultDueDate())
  )
  const [applicant, setApplicant] = useState(initialApplicant || null)
  const [assignees, setAssignees] = useState<string[]>(() =>
    currentUser?.employeeId ? [currentUser.employeeId] : []
  )
  const isValid = title.trim() !== ''

  const { data } = useQuery(GET_TASK, {
    variables: {
      taskId,
    },
    skip: !isEdit,
  })

  // Reset form when taskId is null
  useEffect(() => {
    if (!taskId && opened) {
      setTitle('')
      setDescription('')
      setStatus(TaskStatus.NOT_STARTED)
      setPriority(TaskPriority.LOW)
      setType(TaskType.OTHER)
      const defaultDue = defaultDueDate()
      setDueDate(defaultDue)
      setReminderDate(computeDefaultReminder(defaultDue))
      setApplicant(initialApplicant || null)
      setAssignees(currentUser?.employeeId ? [currentUser.employeeId] : [])
    }
  }, [opened, taskId, initialApplicant, currentUser?.employeeId])

  // Set form values when taskId is provided
  useEffect(() => {
    if (data?.task && taskId) {
      setTitle(data.task.title)
      setDescription(data.task.description || '')
      setStatus(data.task.status)
      setPriority(data.task.priority)
      setType(data.task.type)
      setDueDate(data.task.dueDate)
      setReminderDate(data.task.reminderDate)
      setApplicant(data.task.applicant)
      setAssignees(data.task.assignees.map((a) => a.employeeId))
    }
  }, [data, taskId])

  // Picking/clearing a due date resets the reminder to its default.
  const handleDueChange = (date) => {
    setDueDate(date)
    setReminderDate(date ? computeDefaultReminder(date) : null)
  }

  // Prefill the form from a task template (mirrors the server's relative-to-now
  // due/reminder math; static assignees only — the applicant's recruiter is
  // resolved server-side via includeAssignedEmployee, not here).
  const applyTemplate = (template) => {
    setTitle(template.title ?? '')
    setDescription(template.description ?? '')
    setType(template.type)
    setPriority(template.priority)
    setAssignees(template.assignees?.map((a) => a.employeeId) ?? [])
    const now = dayjs()
    const due =
      template.defaultDueInHours != null
        ? now.add(template.defaultDueInHours, 'hour').toDate()
        : null
    setDueDate(due)
    // Match manual due entry: fall back to the default reminder when the
    // template doesn't specify one.
    setReminderDate(
      template.defaultReminderInHours != null
        ? now.add(template.defaultReminderInHours, 'hour').toDate()
        : computeDefaultReminder(due)
    )
  }

  const goToApplicantDetails = () => {
    if (!applicant?.applicantId) return
    handleClose()
    navigate(
      routes.applicantDetails({ id: applicant.applicantId, tab: 'application' })
    )
  }

  const [createTask, { loading }] = useMutation(CREATE_TASK, {
    onCompleted: () => {
      handleClose()
      if (callRefetch) callRefetch()
      applicantDrawer?.triggerRefresh?.()
    },
    onError: () => {
      toast('Error creating task', 'error')
    },
  })

  const [updateTask, { loading: updateLoading }] = useMutation(UPDATE_TASK, {
    // GetActivityLogs: a note that spawned this task shows its status.
    refetchQueries: ['Notifications', 'GetActivityLogs'],
    onCompleted: () => {
      handleClose()
      if (callRefetch) callRefetch()
      applicantDrawer?.triggerRefresh?.()
    },
    onError: () => {
      toast('Error updating task', 'error')
    },
  })

  const handleSubmit = useCallback(() => {
    if (isValid) {
      if (isEdit) {
        updateTask({
          variables: {
            taskId,
            input: {
              title,
              type,
              priority,
              status,
              description,
              applicantId: applicant?.applicantId || null,
              assigneeIds: assignees.length > 0 ? assignees : null,
              dueDate: dueDate || null,
              reminderDate: reminderDate || null,
            },
          },
        })
      } else {
        createTask({
          variables: {
            input: {
              title,
              type,
              priority,
              status,
              ...(description && { description }),
              ...(applicant?.applicantId && {
                applicantId: applicant?.applicantId,
              }),
              ...(assignees.length > 0 && { assigneeIds: assignees }),
              ...(dueDate && { dueDate }),
              ...(reminderDate && { reminderDate }),
            },
          },
        })
      }
    }
  }, [
    isValid,
    title,
    description,
    applicant,
    assignees,
    type,
    priority,
    status,
    dueDate,
    reminderDate,
    createTask,
    updateTask,
    isEdit,
    taskId,
  ])

  // Add global event listener to ensure hotkey works even when inputs are focused
  useEffect(() => {
    const handleKeyDown = (event) => {
      const isMac = os === 'macos'
      const modKey = isMac ? event.metaKey : event.ctrlKey

      if (modKey && event.key === 'Enter') {
        event.preventDefault()
        handleSubmit()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [handleSubmit, os])

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      yOffset={100}
      overlayProps={{
        backgroundOpacity: 0.2,
        blur: 5,
      }}
      // Explicit zIndex instead of stackId: when opened from the applicant
      // sidebar, this modal lives in Modal.Stack while ApplicantDrawer lives in
      // the separate Drawer.Stack — both default to z-index 200, so the drawer
      // (painted later) covered the modal. zIndex={210} renders it above
      // stack-managed drawers (~201+). Mantine ignores an explicit zIndex when a
      // stackId is also set, so stackId must stay removed. Mirrors EmailSenderModal.
      zIndex={210}
      size="60%"
      withCloseButton={false}
      classNames={{
        body: 'p-0',
      }}
    >
      <div className="flex">
        {/* Left: content */}
        <div className="flex min-w-0 flex-1 flex-col gap-2 p-4">
          {!isEdit && <TaskTemplatePicker onApply={applyTemplate} />}
          <TextInput
            variant="unstyled"
            placeholder="Task title"
            className="w-full font-semibold"
            value={title}
            onChange={(event) => setTitle(event.currentTarget.value)}
            size="lg"
            data-testid="task-title-input"
          />
          <Textarea
            variant="unstyled"
            placeholder="Description"
            autosize
            minRows={6}
            maxRows={16}
            value={description}
            onChange={(event) => setDescription(event.currentTarget.value)}
            size="sm"
          />
        </div>

        {/* Right: properties rail */}
        <div className="flex w-[290px] flex-shrink-0 flex-col gap-3 border-l border-doubleNickel-gray-200 bg-doubleNickel-gray-50 p-4">
          <div className="flex flex-col gap-2.5">
            <RailRow label="Status">
              <StatusMenu status={status} setStatus={setStatus} />
            </RailRow>
            <RailRow label="Priority">
              <PriorityMenu priority={priority} setPriority={setPriority} />
            </RailRow>
            <RailRow label="Type">
              <TypeMenu type={type} setType={setType} />
            </RailRow>
            <RailRow label="Assignee">
              <AssigneeMenu assignees={assignees} setAssignees={setAssignees} />
            </RailRow>
            <RailRow label="Applicant">
              <ApplicantMenu
                applicant={applicant}
                setApplicant={setApplicant}
                onNavigate={
                  applicant?.applicantId ? goToApplicantDetails : null
                }
              />
            </RailRow>
            <RailRow label="Due" align="start">
              <DueControl dueDate={dueDate} onChange={handleDueChange} />
            </RailRow>
            <RailRow label="Reminder" align="start">
              <ReminderControl
                dueDate={dueDate}
                reminderDate={reminderDate}
                onChange={setReminderDate}
              />
            </RailRow>
          </div>

          <div
            className={cn(
              'mt-auto flex flex-row justify-end pt-2',
              !isValid && 'opacity-70'
            )}
          >
            <DNButton
              variant="primary"
              onClick={handleSubmit}
              loading={isEdit ? updateLoading : loading}
            >
              {isEdit ? 'Update task' : 'Create task'}
              <div className="flex flex-row items-center text-doubleNickel-gray-200">
                {os === 'macos' ? '⌘' : 'Ctrl'}
                <IconCornerDownRight className="h-3 w-3 scale-x-[-1] fill-none stroke-doubleNickel-gray-200" />
              </div>
            </DNButton>
          </div>
        </div>
      </div>
    </Modal>
  )
}

export default TaskModal
